import React from 'react';
import styled, { keyframes } from 'styled-components';
import { shimmerText } from '../chat/activityShimmer';

export interface LoadingMessageProps {
  // When provided, displayed next to the spinner. Use for contextual
  // status that the tool indicators don't already cover — e.g. the
  // OCR/extraction phase before chat generation actually starts, where
  // no tool_action item is emitted.
  loadingMessage?: string;
}

function LoadingMessage({ loadingMessage }: LoadingMessageProps) {
  // Spinner-only by default. The ONE exception is the file-processing phase
  // (upload + text extraction), which runs before generation starts and has
  // no tool-action block of its own — surface that text so the user knows what
  // is taking time. Generation/reasoning/tool statuses stay spinner-only.
  const msg = loadingMessage || '';
  // Deep-research planning has no block of its own either (like file-processing),
  // so surface its text instead of a bare spinner.
  const showText = msg.startsWith('Extraindo') || msg.startsWith('Fazendo upload') || msg.startsWith('Criando plano');
  return (
    <Wrapper>
      <Ring />
      {showText && <Label>{msg}</Label>}
    </Wrapper>
  );
}

export default LoadingMessage;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  /* Same geometry as the activity-block headers (Pensando / tool labels):
     15px leading slot, 6px gap, 0.88rem label — one indicator idiom. No left
     padding: the ring's left edge sits on the message text column. */
  padding: 4px 8px 4px 0;
  gap: 6px;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Ring = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => (theme.mode === 'dark'
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.12)')};
  border-top-color: ${({ theme }) => theme.text_secondary || theme.font_color};
  animation: ${spin} 0.75s linear infinite;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Label = styled.span`
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  /* Always an in-progress state when text is shown — shimmer like the rest. */
  ${shimmerText}
`;
